<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import { ref, computed } from 'vue'
import { ChevronRightIcon } from '@heroicons/vue/24/outline'
import { usePage, router } from '@inertiajs/vue3'
import HeadingTwo from '@/Components/HeadingTwo.vue'
import Paragraph from '@/Components/Paragraph.vue'

const props = defineProps({
    incomes: {
        type: Array,
        required: true,
    },

    product: {
        type: Object,
        required: true,
    },
})

const selected_incomes = ref([])

const toggleIncomeSelection = (income) => {
    const index = selected_incomes.value.findIndex((selected) => selected.name === income.name)
    if (index === -1) {
        selected_incomes.value.push(income)
    } else {
        selected_incomes.value.splice(index, 1)
    }
}

const checkIfCanContinue = computed(() => {
    return selected_incomes.value.length > 0 && usePage().props.auth.user
})

function submit() {
    if (!checkIfCanContinue.value) {
        return
    }

    const incomesPayload = selected_incomes.value.map((income) => ({ income_id: income.id }))

    router.post(route('orders.store'), {
        incomes: incomesPayload,
    })
}
</script>

<template>
    <AppLayout>
        <template #content>
            <section class="py-12 sm:py-24">
                <div class="mx-auto max-w-3xl px-6 lg:px-8">
                    <div class="">
                        <HeadingTwo
                            class="!mt-0"
                            value="Daňové přiznání"
                        />

                        <Paragraph> Vyberte příjmy, které chcete zahrnout do daňového přiznání za rok 2024. </Paragraph>

                        <div class="mt-8 flex flex-col items-center justify-center">
                            <div class="card relative text-black transition-colors duration-200 dark:text-white">
                                Cena daňového přiznání je <span class="text-2xl font-bold">{{ product.price }} Kč.</span>
                            </div>
                        </div>
                    </div>

                    <div class="mt-16 sm:mt-20 lg:mt-24">
                        <dl class="mx-auto grid max-w-xl grid-cols-1 gap-4 lg:max-w-none lg:grid-cols-2">
                            <div
                                v-for="income in incomes"
                                :key="income.name"
                                :class="[
                                    selected_incomes.includes(income)
                                        ? 'bg-sky-400 ring-2 ring-sky-500 dark:bg-sky-950'
                                        : 'ring-2 ring-transparent',
                                    'card relative transition-colors duration-200 hover:bg-sky-400 hover:ring-sky-500 dark:hover:bg-sky-950',
                                ]"
                            >
                                <dt class="text-base/7 font-semibold dark:text-zinc-200">
                                    {{ income.name }}
                                </dt>

                                <dd class="mt-2 text-base/7 text-zinc-700 dark:text-zinc-400">{{ income.description }}</dd>

                                <button
                                    @click="toggleIncomeSelection(income)"
                                    class="absolute inset-0 z-10 h-full w-full cursor-pointer"
                                    :aria-label="selected_incomes.includes(income) ? 'Odebrat ' + income.name : 'Přidat ' + income.name"
                                />
                            </div>
                        </dl>

                        <div
                            class="relative mt-10 flex items-center justify-center"
                            v-if="checkIfCanContinue"
                        >
                            <button
                                @click="submit"
                                aria-current="Podat přiznání za rok 2024"
                                class="relative flex items-center rounded-lg border border-cyan-300 bg-cyan-600 px-4 py-2"
                            >
                                <ChevronRightIcon class="mr-2 size-5 text-white sm:size-7" />

                                <span class="text-sm font-medium text-white transition-colors duration-200 sm:text-base"> Pokračovat </span>
                            </button>
                        </div>

                        <div
                            class="relative mt-10 flex items-center justify-center"
                            v-else-if="!usePage().props.auth.user"
                        >
                            <Link
                                :href="route('login')"
                                aria-current="Přihlásit se"
                                class="relative flex items-center rounded-lg border border-red-300 bg-red-600 px-4 py-2"
                            >
                                <span class="text-sm font-medium text-white transition-colors duration-200 sm:text-base">
                                    Musíte se nejdříve přihlásit
                                </span>
                            </Link>
                        </div>

                        <div
                            class="relative mt-10 flex items-center justify-center"
                            v-else
                        >
                            <button
                                aria-current="Podat přiznání za rok 2024"
                                class="relative flex items-center rounded-lg border border-red-300 bg-red-600 px-4 py-2"
                                disabled
                            >
                                <ChevronRightIcon class="mr-2 size-5 text-white sm:size-7" />

                                <span class="text-sm font-medium text-white transition-colors duration-200 sm:text-base">
                                    Vyberte příjmy
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </template>
    </AppLayout>
</template>
